import React, { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { termsUrl } from '~/config/settings/worktango.settings';
import { mui } from '~/tools/mui';
import { jss } from '~/tools/jss';
import { accountActions } from '~/behaviors/actions/account.actions';
import { useTranslation } from '~/behaviors/languages';
import { useAppDispatch } from '~/behaviors/redux';
import Button from '~/components/Button';
import { PasswordInput, StatusDialog } from '../UI.shared';

const useStyles = jss.makeStyles((theme) => ({
  cardContent: {
    padding: 24,
    width: 476,
    textAlign: 'center',
  },
  formField: {
    marginTop: 24,
  },
  inputLabel: {
    fontSize: 14,
  },
  input: {
    fontSize: 14,
    height: 40,
    boxSizing: 'border-box',
    padding: '8px 16px',
  },
  termsContainer: {
    textAlign: 'left',
    marginTop: 24,
  },
  link: {
    fontFamily: theme.typography.fontFamily,
    color: theme.palette.primary.main,
    fontWeight: 500,
    textDecoration: 'none',
    '&:hover': {
      color: theme.legacyPalette.darkaccent,
    },
  },
  submitBtnContainer: {
    margin: '24px 0px 0px 0px',
  },
}));

const defaultDialogState = {
  title: '',
  message: '',
  status: 'info',
  isVisible: false,
  onClose: () => {},
};

const ActivatePage = () => {
  // ------------------------------------------------------------------ State --
  const styles = useStyles();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { activation_code: activationCode } = useParams();

  const [email, setEmail] = useState();
  const [companyName, setCompanyName] = useState();
  const [hasSSO, setHasSSO] = useState(true);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [dialog, setDialog] = useState(defaultDialogState);
  const title =
    !email || !companyName
      ? t('welcome')
      : t('user.activation.description', {
          userEmail: email,
          companyName,
        });
  const isInvitedUserValid = email && companyName;

  const { register, errors: formErrors, handleSubmit } = useForm({ mode: 'onChange' });

  // ---------------------------------------------------------------- Helpers --
  const showErrorDialog = useCallback(
    (message) => {
      setDialog({
        title: t('oops.something.went.wrong'),
        message: message,
        status: 'error',
        isVisible: true,
        onClose: () => setDialog(defaultDialogState),
      });
    },
    [setDialog, t],
  );

  // -------------------------------------------------------------- Lifecycle --
  useEffect(() => {
    dispatch(accountActions.getActivationDetails(activationCode)).then((action) => {
      // Error is appended to ui.account.error when action is rejected to be
      // picked up by login page.
      if (action.error) {
        navigate('/login');
      } else {
        const activationDetails = action.payload;

        setEmail(activationDetails.email);
        setCompanyName(activationDetails.companyName);
        setHasSSO(activationDetails.hasSSO);
      }
    });
  }, [dispatch, activationCode, navigate, showErrorDialog]);

  // ----------------------------------------------------------------- Events --
  const handleFormSubmit = (formData) => {
    dispatch(
      accountActions.activateUser({
        activationCode,
        firstName: formData.firstName,
        lastName: formData.lastName,
        password: formData.password,
      }),
    ).then((action) => {
      if (action.error) return showErrorDialog(action.error.message);

      setDialog({
        title: t('user.activation.title'),
        message: t('user.activation.success'),
        status: 'success',
        isVisible: true,
        onClose: () => {
          navigate('/login');
          navigate(0);
        },
      });
    });
  };

  // -------------------------------------------------------------- Interface --
  return (
    <mui.CardContent className={styles.cardContent} data-testid="activate-page">
      <StatusDialog
        title={dialog.title}
        status={dialog.status}
        open={dialog.isVisible}
        onClose={() => dialog.onClose()}
      >
        {dialog.message}
      </StatusDialog>

      <mui.Typography className={styles.description}>{title}</mui.Typography>

      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <mui.TextField
          fullWidth
          name="firstName"
          label={t('profile.fname')}
          size="small"
          inputRef={register({ required: 'form.isRequired' })}
          error={!!formErrors.firstName}
          helperText={t(formErrors['firstName.message'])}
          className={styles.formField}
          inputProps={{ 'data-testid': 'firstName' }}
          variant="outlined"
          InputProps={{ classes: { input: styles.input } }}
          InputLabelProps={{ classes: { root: styles.inputLabel } }}
        />

        <mui.TextField
          fullWidth
          name="lastName"
          label={t('profile.lname')}
          size="small"
          inputRef={register({ required: 'form.isRequired' })}
          error={!!formErrors.lastName}
          helperText={t(formErrors['lastName.message'])}
          className={styles.formField}
          inputProps={{ 'data-testid': 'lastName' }}
          variant="outlined"
          InputProps={{ classes: { input: styles.input } }}
          InputLabelProps={{ classes: { root: styles.inputLabel } }}
        />

        {!hasSSO && (
          <PasswordInput
            name="password"
            size="small"
            toggleVisibility
            checkStrength
            label={t('profile.password')}
            inputRef={register({
              required: 'form.isRequired',
            })}
            error={!!formErrors.password}
            helperText={t(formErrors['currentPassword.message'])}
            className={styles.formField}
            inputProps={{ 'data-testid': 'password' }}
            variant="outlined"
            InputProps={{ classes: { input: styles.input } }}
            InputLabelProps={{ classes: { root: styles.inputLabel } }}
          />
        )}

        <div className={styles.termsContainer}>
          <mui.Checkbox
            checked={isConfirmed}
            onChange={() => setIsConfirmed(!isConfirmed)}
            color="primary"
            inputProps={{
              'aria-label': 'secondary checkbox',
              'data-testid': 'accept-terms',
            }}
          />
          <mui.Typography variant="caption" className={styles.termsOfUse}>
            <span>
              {t('term.accept')}
              &nbsp;
            </span>
            <mui.Link rel="noreferrer" target="_blank" href={termsUrl}>
              {t('term.title')}
            </mui.Link>
          </mui.Typography>
        </div>

        <div className={styles.submitBtnContainer}>
          <Button
            disabled={!isConfirmed || !isInvitedUserValid}
            variant="contained"
            color="primary"
            type="submit"
            fullWidth
            data-testid="submit"
          >
            {t('button.save')}
          </Button>
        </div>
      </form>
    </mui.CardContent>
  );
};

export default ActivatePage;
